import { useId, useMemo, useRef, useState, useCallback, useEffect, Fragment  } from 'react';
import { connect } from 'react-redux';

import { 
  PUBLISH_SCHEDULE, Email, SET_LOADING, GET_PROJECT_SCHEDULE_CONTACTS, UNPUBLISH_SCHEDULE,
  SET_SCHEDULE_UNLOCKED_CONFIRMATION_SHOWING, SET_EDIT_PROJECT_DIALOGUE_ID, SET_SCHEDULE_PUBLISHED_CONFIRMATION_SHOWING,
} from '../actions/types';
import { siteboardAction, setPublishScheduleDialogueId } from '../actions/defaultActions';
import { objectValuesToList, objectKeysToList, keyCountInObject, } from '../Util';
import { format } from 'date-fns';

import PopupWindow from './PopupWindow';
import UserAvatar from './UserAvatar';
import CollapsibleContainer from './CollapsibleContainer';
import PushButton from './PushButton';
import ProjectContacts from './ProjectContacts';
import AddProjectContact from './AddProjectContact';

import { Eye, CalendarCheck2, UserRound, ArrowUpRight, Check, CalendarDays, Clock, Lock, Send } from 'lucide-react';
import SendIcon from '../icons/sendIcon';
import CheckboxIcon from '../icons/checkboxIcon';
import ExpandDownIcon from '../icons/expandDownIcon';
import AddIcon from '../icons/addIcon';
import UnlockIcon from '../icons/unlockIcon';

// test cases
const testNoEmails = false;
const testViewersScroll = false;
 
const PAGE_PUBLISH = 'PAGE_PUBLISH';
const PAGE_ADD_EMAILS = 'PAGE_ADD_EMAILS';

const PublishScheduleDialogue = ({ 
  _users,
  _projects,
  _schedules,
  _activities,
  _publishScheduleDialogueId,
  _subscriptionContacts,
  _projectsContactEmails,
  _contactSharedScheduleStats,
  setPublishScheduleDialogueId,
  siteboardAction 
}) => {

  //const [showProjectMembers, setShowProjectMembers] = useState(false);
  //const [emailContactsListOpen, setEmailContactsListOpen] = useState(false);
  const [currentScreen, setCurrentScreen] = useState(PAGE_PUBLISH);
  const [hasChangedScreen, setHasChangedScreen] = useState(false);
  const [unlockClickedOnce, setUnlockClickedOnce] = useState(false);

  const closeDialogue = useCallback(() => {
    setPublishScheduleDialogueId(null);
  }, [setPublishScheduleDialogueId]);

  useEffect(() => {
    if(currentScreen === PAGE_PUBLISH) return;
    setHasChangedScreen(true);
  }, [currentScreen]);

  useEffect(() => {
    //setEmailContactsListOpen(false);
    //setShowProjectMembers(false);
  }, [currentScreen]);

  // Fetch schedule contacts from the server. If we already have them this will do nothing
  useEffect(() => {
    const selectedSchedule = _schedules[_publishScheduleDialogueId];
    if(selectedSchedule === undefined) return;
    siteboardAction(GET_PROJECT_SCHEDULE_CONTACTS, {projectId: selectedSchedule.projectId});
  }, [siteboardAction, _publishScheduleDialogueId, _schedules]);

  const selectedSchedule = _schedules[_publishScheduleDialogueId];
  const selectedProject = _projects[selectedSchedule.projectId];

  const viewers = useMemo(() => {
    const schedule = _schedules[_publishScheduleDialogueId];
    const project = _projects[schedule.projectId];
    if(!project || !schedule || !(project.subscriptionId in _subscriptionContacts)) return [];

    let sharedContactScheduleStatsTotal = 0;
    let contactsViewedScheduleTotal = 0;
    let contactsConfirmedScheduleTotal = 0;
    const viewedScheduleContactEmailsMap = {};
    const lastViewedTimeContactEmailsMap = {};
    const viewers = [];
    for(const scheduleId in _contactSharedScheduleStats) {
      if(scheduleId !== schedule.id) continue;
      for(const email in _contactSharedScheduleStats[scheduleId]) {
        sharedContactScheduleStatsTotal++;
        if(_contactSharedScheduleStats[scheduleId][email].isViewed) {
          contactsViewedScheduleTotal++;
          viewedScheduleContactEmailsMap[email] = '';
          lastViewedTimeContactEmailsMap[email] = _contactSharedScheduleStats[scheduleId][email].viewedTimestamp;
          const contact = email in _subscriptionContacts[project.subscriptionId] ? _subscriptionContacts[project.subscriptionId][email] : undefined;
          viewers.push({
            id: email,
            firstName: contact ? contact.firstName : '',
            lastName: contact ? contact.lastName : '',
            companyName: contact ? contact.companyName : '',
            email: email,
            viewedTimestamp: _contactSharedScheduleStats[scheduleId][email].viewedTimestamp,
          });
        }
      }
    }
    if(testViewersScroll) {
      const realViewers = [...viewers];
      for(let i = 0; i < 12; i++) {
        viewers.push(...realViewers);
      }
    }
    return viewers;
  }, [_subscriptionContacts, _contactSharedScheduleStats, _projects, _schedules, _publishScheduleDialogueId]);

  if(selectedSchedule === undefined) return (<></>);
  if(selectedProject === undefined) return (<></>);

  const selectedContacts = (() => {
    if(testNoEmails) return [];
    if(!(selectedProject.subscriptionId in _subscriptionContacts)) return [];
    const projectContacts = objectKeysToList(_projectsContactEmails[selectedProject.id] || [])
      .filter((email) => email in _subscriptionContacts[selectedProject.subscriptionId])
      .map((email) => _subscriptionContacts[selectedProject.subscriptionId][email]);
    return projectContacts;
  })();
  const totalOptedOutContacts = selectedContacts.reduce((count, contact) => {
    return contact.hasUnsubscribed === true ? count + 1 : count;
  }, 0);

  const projectMembersReceivingEmail = [];
  for(const userId in selectedProject.members) {
    if(!(userId in _users)) continue;
    projectMembersReceivingEmail.push(_users[userId]);
  }

  const totalContactsReceivingEmail = selectedProject.contactCount - totalOptedOutContacts;
  const totalPeopleReceivingEmail = projectMembersReceivingEmail.length + totalContactsReceivingEmail;

  //const selectedActivities = objectValuesToList(_activities).filter(activity => activity.scheduleId === selectedSchedule.id);
  //const responsibleMap = {};
  //for(const activity of selectedActivities) {
  //  if(typeof activity.responsible !== 'string') continue;
  //  if(activity.responsible.match(/^\s*$/) !==  null) continue; // only whitespace
  //  responsibleMap[activity.responsible] = '';
  //}

  const onBackButton = currentScreen === PAGE_PUBLISH ? null : () => { setCurrentScreen(PAGE_PUBLISH); };
  let publishedBy = '';
  let publisherEmail = '';
  try {
    publishedBy = _users[selectedSchedule.publishedByUserId].firstName + ' ' + _users[selectedSchedule.publishedByUserId].lastName;
    publisherEmail = _users[selectedSchedule.publishedByUserId].email;
  } catch(error) {}
  
  const getScheduleViewTotal = () => {
    try {
      let sharedContactScheduleStatsTotal = 0;
      let contactsViewedScheduleTotal = 0;
      let contactsConfirmedScheduleTotal = 0;
      for(const scheduleId in _contactSharedScheduleStats) {
        if(scheduleId !== selectedSchedule.id) continue;
        for(const email in _contactSharedScheduleStats[scheduleId]) {
          sharedContactScheduleStatsTotal++;
          if(_contactSharedScheduleStats[scheduleId][email].isViewed) {
            contactsViewedScheduleTotal++;
          }
          if(_contactSharedScheduleStats[scheduleId][email].isConfirmed) {
            contactsConfirmedScheduleTotal++;
          }
        }
      }
      return contactsViewedScheduleTotal;
    } catch(error) {}
    return 0;
  };

  return (
    <PopupWindow 
      title={currentScreen === PAGE_PUBLISH ? 'Publish & Send Schedule' : 'Add to Distribution List'}
      onBackButton={onBackButton}
      closeDialogue={closeDialogue}
      bgStyle='bg-neutral-800'
    >
      {currentScreen === PAGE_PUBLISH && selectedSchedule.isPublished === true && 
        <div className={`flex flex-col gap-4 max-w-screen-lg text-white`}>
          <div className='w-[100vw] max-w-full'>
            <PublishedScheduleCard
              viewCount={getScheduleViewTotal()}
              publishedBy={publishedBy}
              publisherEmail={publisherEmail}
              publishedAt={selectedSchedule.publishedTimestamp}
              viewers={viewers}
            />
          </div>

          <div className='relative isolate overflow-hidden rounded-xl bg-neutral-900 p-5 sm:p-6'>
            <div className={`flex flex-col items-start gap-4 transition-opacity duration-200 opacity-100`}>
              <div className='flex flex-col gap-1.5'>
                <h3 className='flex items-center gap-1.5 font-semibold text-white'>
                  <Lock size='1.2em' /> Published schedule is locked
                </h3>

                <p className='max-w-xl leading-relaxed text-white/60'>
                  This schedule is locked to prevent accidental changes. <br/>Unlock it to make edits, then publish it again when you’re ready.
                </p>
              </div>

              <PushButton
                textColor={unlockClickedOnce ? 'black' :'white'}
                bgColor={unlockClickedOnce ? '#f9a928' :'#471cff'}
                hoverBgColor={unlockClickedOnce ? '#f79800' :'#603aff'}
                onClick={() => {
                  if(unlockClickedOnce) {
                    siteboardAction(UNPUBLISH_SCHEDULE, {
                      scheduleId: selectedSchedule.id,
                      projectId: selectedProject.id,
                      subscriptionId: selectedProject.subscriptionId,
                    }, (validOutput) => {
                      siteboardAction(SET_SCHEDULE_UNLOCKED_CONFIRMATION_SHOWING, { isOpen: true }); 
                      closeDialogue();
                    });
                  } else {
                    setUnlockClickedOnce(true);
                  }
                }}
              >
                <div className='flex items-center gap-2 text-left'>
                  <UnlockIcon size={16} color={unlockClickedOnce ? '#000' : '#fff'} />
                  <span className='font-semibold'>
                    {unlockClickedOnce ? 'Click again to unlock' : 'Unlock to edit'}
                  </span>
                </div>
              </PushButton>
            </div>
          </div>

        </div>
      }

      <div className={`
          flex flex-col gap-8 max-w-screen-sm text-white animate-right-fade 
          ${currentScreen === PAGE_PUBLISH && selectedSchedule.isPublished !== true ? '' : 'hidden'}`
        }
      >
        <div className='w-[100vw] max-w-full'>
          Email this schedule to the following <b>{totalPeopleReceivingEmail}</b> {totalPeopleReceivingEmail === 1 ? 'person' : 'people'}:
        </div>

        <div className='flex flex-col gap-2'>
          <PushButton 
            textColor='white'
            bgColor={'#444'}
            hoverBgColor='#555'
            onClick={() => {
              siteboardAction(SET_EDIT_PROJECT_DIALOGUE_ID, { projectId: selectedProject.id, tabName: 'Team' });
            }}
          >
            <div className='flex items-center justify-between w-full text-[1.2em] font-normal'>
              <div className='flex items-center gap-2'>
                <b>{projectMembersReceivingEmail.length}</b> 
                Project Member{projectMembersReceivingEmail.length !== 1 ? 's' :'' }
              </div>
              <ArrowUpRight />
            </div>
          </PushButton>
          {selectedProject.contactCount === 0 
            ?
              <div className={`flex flex-col justify-start items-start gap-4 bg-neutral-700 p-6 rounded-lg`}>
                <div className='flex flex-col gap-1'>
                  <h3 className='text-left font-bold text-[1.2em]'>Want others to stay in the loop?</h3>
                  <div>Add any email address and they’ll get a copy when you publish this schedule.</div>
                </div>
                <PushButton 
                  textColor='black'
                  bgColor='#f9a928'
                  hoverBgColor='#f79800'
                  onClick={() => {
                    setCurrentScreen(PAGE_ADD_EMAILS);
                  }}
                >
                  <div className='flex items-center gap-1' ><AddIcon color='#000' /> Add Emails to Distribution List</div>
                </PushButton>
              </div>
            :
              <div className='flex flex-col gap-0'>
                <PushButton 
                  textColor='white'
                  bgColor={'#444'}
                  hoverBgColor='#555'
                  onClick={() => {
                    siteboardAction(SET_EDIT_PROJECT_DIALOGUE_ID, { projectId: selectedProject.id, tabName: 'Distribution' });
                  }}
                >
                  <div className='flex items-center justify-between w-full text-[1.2em] font-normal'>
                    <div className='flex items-center gap-2 '>
                      <b>{totalContactsReceivingEmail}</b>
                      Distribution List Contacts 
                    </div>
                    <ArrowUpRight />
                  </div>
                </PushButton>
                <PushButton 
                  textColor='black'
                  bgColor='#f9a928'
                  hoverBgColor='#f79800'
                  onClick={() => {
                    setCurrentScreen(PAGE_ADD_EMAILS);
                  }}
                >
                  <div className='flex items-center gap-1' ><AddIcon color='#000' /> Add Emails to Distribution List</div>
                </PushButton>
              </div>
          }
        </div>

        <PushButton 
          textColor='white'
          bgColor='#471cff'
          hoverBgColor='#603aff'
          disabled={selectedSchedule.isPublished}
          title={selectedSchedule.isPublished ? 'Unlock the schedule before sending' : ''}
          onClick={() => {
            siteboardAction(PUBLISH_SCHEDULE, {
              scheduleId: selectedSchedule.id,
              projectId: selectedProject.id,
              subscriptionId: selectedProject.subscriptionId,
            }, (output) => { 
              siteboardAction(SET_SCHEDULE_PUBLISHED_CONFIRMATION_SHOWING, { isOpen: true }, (validOutput) => {});
              closeDialogue(); 
            });
          }}
        >
          <div className='flex items-center gap-x-[6px]'>
            <Send size='1.2em'/>
            <div>Publish & Send Schedule{/* to {totalPeopleReceivingEmail} {totalPeopleReceivingEmail === 1 ? 'Contact' : 'Contacts'}*/}</div>
          </div>
        </PushButton>
      </div>

      {/* Add to Distribution list page */}
      <div className={`max-w-screen-sm text-white animate-left-fade ${currentScreen === PAGE_ADD_EMAILS ? '' : 'hidden'}`}>
        <div className='w-[90vw] max-w-full' />
        <AddProjectContact 
          projectId={selectedProject.id}
          onClose={null}
          onDone={()=> { setCurrentScreen(PAGE_PUBLISH); }}
          showBorder={false}
        />
      </div>
    </PopupWindow>
  );
};

function formatTimeAgo(timestamp) {
  if (timestamp == null) return null;

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return null;

  const seconds = Math.max(
    0,
    Math.floor((Date.now() - date.getTime()) / 1000)
  );

  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;

  return `${Math.floor(seconds / 86400)}d ago`;
}

function formatTimestamp(timestamp) {
  if (timestamp == null) return null;

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return null;

  const formattedDate = new Intl.DateTimeFormat("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
  }).format(date);

  const formattedTime = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date);

  return `${formattedDate} @ ${formattedTime}`;
}

function PublishedScheduleCard({
  viewCount = 0,
  publishedBy,
  publisherEmail,
  publishedAt,
  viewers = [],
}) {
  const viewerListBreakpoint = 3;

  const [isExpanded, setIsExpanded] = useState(true);
  const [showAllViewers, setShowAllViewers] = useState(false);
  const [filterText, setFilterText] = useState("");

  const viewerListId = useId();
  const searchInputId = useId();

  const normalizedFilter = filterText.trim().toLowerCase();

  const filteredViewers = viewers.filter((viewer) =>
    ["firstName", "lastName", "companyName", "email"].some((key) =>
      (viewer[key] || "").toLowerCase().includes(normalizedFilter)
    )
  );

  const visibleViewers = showAllViewers
    ? filteredViewers
    : filteredViewers.slice(0, viewerListBreakpoint);

  const remainingCount = Math.max(
    filteredViewers.length - viewerListBreakpoint,
    0
  );

  const publishedTimeAgo = formatTimeAgo(publishedAt);
  const publishedDate = formatTimestamp(publishedAt);

  return (
    <section className="w-full rounded-2xl bg-neutral-900 p-5 text-white sm:p-6">
      <style>{`
        @keyframes schedulePublishedPop {
          0% { opacity: 0; transform: scale(0.75); }
          65% { opacity: 1; transform: scale(1.08); }
          100% { opacity: 1; transform: scale(1); }
        }

        @keyframes schedulePublishedCheck {
          from { stroke-dashoffset: 24; }
          to { stroke-dashoffset: 0; }
        }

        @media (prefers-reduced-motion: no-preference) {
          .schedule-published-icon {
            animation: schedulePublishedPop 450ms ease-out both;
          }

          .schedule-published-check {
            stroke-dasharray: 24;
            stroke-dashoffset: 24;
            animation: schedulePublishedCheck 350ms ease-out 180ms forwards;
          }
        }
      `}</style>

      <div className="grid min-w-0 grid-cols-1 gap-6 md:grid-cols-2 md:gap-8">
        {/* Publication information */}
        <div className="min-w-0">
          <h2 className="flex items-center gap-3 text-base font-semibold text-emerald-300">
            <span
              aria-hidden="true"
              className="
                schedule-published-icon
                flex h-9 w-9 shrink-0 items-center justify-center
                rounded-full bg-emerald-400/10
                ring-1 ring-inset ring-emerald-400/20
              "
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-5 w-5 text-emerald-400"
              >
                <path
                  className="schedule-published-check"
                  d="m5 12 4 4L19 6"
                />
              </svg>
            </span>
            Schedule published
          </h2>

          <div className="mt-6">
            <div className="text-4xl font-semibold tracking-tight tabular-nums">
              {viewCount.toLocaleString()}
            </div>
            <div className="mt-1 text-neutral-400">
              {viewCount === 1 ? "Schedule view" : "Schedule views"}
            </div>
          </div>

          <dl className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="min-w-0">
              <dt className="text-neutral-500 flex items-center gap-1.5">
                <UserRound size='1em' /> 
                Published by
              </dt>
              <dd className="mt-1">
                <div className="break-words font-medium text-neutral-100">
                  {publishedBy}
                </div>

                {publisherEmail && (
                  <div className="mt-0.5 break-all text-neutral-300">
                    {publisherEmail}
                  </div>
                )}
              </dd>
            </div>

            <div className="min-w-0">
              <dt className="text-neutral-500 flex items-center gap-1.5">
                <Clock size='1em' /> 
                Published on
              </dt>
              <dd className="mt-1">
                {publishedDate ? (
                  <time
                    dateTime={new Date(publishedAt).toISOString()}
                    className="block text-neutral-100"
                  >
                    {publishedDate}
                  </time>
                ) : (
                  <span className="text-neutral-300">Date unavailable</span>
                )}

                {publishedTimeAgo && (
                  <div className="mt-0.5 text-neutral-300">
                    {publishedTimeAgo}
                  </div>
                )}
              </dd>
            </div>
          </dl>
        </div>

        {/* Viewers: below on mobile, right on lg screens */}
        <div className="min-w-0 border-t border-white/10 pt-4 md:border-l md:border-t-0 md:pl-8 md:pt-0">
          <button
            type="button"
            aria-expanded={isExpanded}
            aria-controls={viewerListId}
            onClick={() => setIsExpanded((previous) => !previous)}
            className="
              flex w-full items-center justify-between gap-3
              rounded-md py-1 text-left
              focus-visible:outline-none focus-visible:ring-2
              focus-visible:ring-violet-400
            "
          >
            <span className="flex items-center gap-2 font-medium">
              Viewers
              <span className="font-normal tabular-nums text-neutral-400">
                {viewers.length.toLocaleString()}
              </span>
            </span>

            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={`
                h-4 w-4 shrink-0 text-neutral-400
                transition-transform duration-200
                motion-reduce:transition-none
                ${isExpanded ? "rotate-180" : ""}
              `}
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
          </button>

          <div id={viewerListId} hidden={!isExpanded}>
            {viewers.length > 1 && (
              <div className="relative mb-2 mt-3">
                <label htmlFor={searchInputId} className="sr-only">
                  Filter viewers by name, company, or email
                </label>

                <svg
                  aria-hidden="true"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="
                    pointer-events-none absolute left-3 top-1/2
                    h-4 w-4 -translate-y-1/2 text-neutral-500
                  "
                >
                  <circle cx="10.5" cy="10.5" r="6.5" />
                  <path d="m16 16 4 4" />
                </svg>

                <input
                  id={searchInputId}
                  type="search"
                  value={filterText}
                  onChange={(event) => {
                    setFilterText(event.target.value);
                    setShowAllViewers(false);
                  }}
                  placeholder="Search viewers…"
                  className="
                    w-full rounded-lg border border-white/30
                    bg-transparent py-2.5 pl-9 pr-12
                    text-neutral-200 placeholder:text-neutral-500
                    transition-colors duration-200
                    focus:border-violet-400/60 focus:outline-none
                    focus:ring-1 focus:ring-violet-400/60
                    [&::-webkit-search-cancel-button]:appearance-none
                  "
                />

                {filterText && (
                  <button
                    type="button"
                    aria-label="Clear viewer search"
                    onClick={() => {
                      setFilterText("");
                      setShowAllViewers(false);
                      document.getElementById(searchInputId)?.focus();
                    }}
                    className="
                      absolute right-0 top-1/2
                      flex h-11 w-11 -translate-y-1/2
                      items-center justify-center rounded-lg
                      text-white/80 transition-colors duration-200
                      hover:text-white
                      focus-visible:outline-none focus-visible:ring-2
                      focus-visible:ring-violet-400
                    "
                  >
                    <svg
                      aria-hidden="true"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      className="h-6 w-6"
                    >
                      <path d="m6 6 12 12M18 6 6 18" />
                    </svg>
                  </button>
                )}
              </div>
            )}

            {viewers.length === 0 ? (
              <p className="py-3 text-neutral-500">
                No viewers yet.
              </p>
            ) : filteredViewers.length === 0 ? (
              <p role="status" className="py-3 text-neutral-500">
                No viewers match your search.
              </p>
            ) : (
              <>
                <ul className={viewers.length > 1 ? "" : "mt-2"}>
                  {visibleViewers.map((viewer) => {
                    const firstName = viewer.firstName?.trim() || "";
                    const lastName = viewer.lastName?.trim() || "";
                    const email = viewer.email?.trim() || "";
                    const companyName = viewer.companyName?.trim() || "";
                    const status = viewer.status?.trim() || "";

                    const hasFullName = Boolean(firstName && lastName);
                    const viewedTimeAgo = formatTimeAgo(
                      viewer.viewedTimestamp
                    );
                    const viewedDate = formatTimestamp(
                      viewer.viewedTimestamp
                    );

                    const initials = hasFullName
                      ? `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
                      : email.charAt(0).toUpperCase() || "?";

                    return (
                      <li
                        key={viewer.id || email}
                        className="
                          group flex items-center gap-2.5 rounded-lg px-2 py-2
                          transition-colors duration-200 hover:bg-white/[0.04]
                        "
                      >
                        <div
                          aria-hidden="true"
                          className="
                            flex h-9 w-9 shrink-0 items-center justify-center
                            rounded-full bg-[#f9a928dd]
                            font-semibold tracking-wide text-neutral-950
                          "
                        >
                          {initials}
                        </div>

                        <div className="relative min-w-0 flex-1 pl-2.5">

                          {hasFullName && (
                            <div className="break-words font-medium text-neutral-100">
                              {firstName} {lastName}
                            </div>
                          )}

                          {(companyName || status) && (
                            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-neutral-200">
                              {companyName && (
                                <span className="break-words">
                                  {companyName}
                                </span>
                              )}

                              {companyName && status && (
                                <span aria-hidden="true">·</span>
                              )}

                              {status && (
                                <span className="break-words text-neutral-300">
                                  {status}
                                </span>
                              )}
                            </div>
                          )}

                          {email && (
                            <div
                              className={
                                hasFullName
                                  ? "break-all text-neutral-200"
                                  : "break-all font-medium text-neutral-200"
                              }
                            >
                              {email}
                            </div>
                          )}

                          {viewedDate && (
                            <time
                              dateTime={new Date(
                                viewer.viewedTimestamp
                              ).toISOString()}
                              className="
                                mt-1 block leading-relaxed
                                text-emerald-400/80 text-sm
                              "
                            >
                              Viewed {viewedTimeAgo}<div className='hidden min-[400px]:inline-block'>, {viewedDate}</div>
                            </time>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>

                {filteredViewers.length > viewerListBreakpoint && (
                  <button
                    type="button"
                    onClick={() => setShowAllViewers((previous) => !previous)}
                    className="
                      mt-2 rounded-md px-2 py-2 font-medium
                      text-violet-400 transition-colors duration-200
                      hover:text-violet-300
                      focus-visible:outline-none focus-visible:ring-2
                      focus-visible:ring-violet-400
                    "
                  >
                    {showAllViewers
                      ? "Show less"
                      : `View more (${remainingCount.toLocaleString()})`}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// define redux state that will be passed in as props
const mapStateToProps = state => { return {
  _users: state.serverSynced.users,
  _projects: state.serverSynced.projects,
  _schedules: state.serverSynced.schedules,
  _activities: state.serverSynced.activities,
  _publishScheduleDialogueId: state.localEphemeral.publishScheduleDialogueId,
  _subscriptionContacts: state.serverSynced.subscriptionContacts,
  _projectsContactEmails: state.serverSynced.projectsContactEmails,
  _contactSharedScheduleStats: state.serverSynced.contactSharedScheduleStats,
}};
export default connect(mapStateToProps, { siteboardAction, setPublishScheduleDialogueId })(PublishScheduleDialogue);

// <div className='w-full flex flex-col'>
//   <PushButton 
//     textColor='white'
//     bgColor={showProjectMembers ? '#555' : '#444'}
//     hoverBgColor='#555'
//     dropdownButton={true}
//     dropdownOpen={showProjectMembers}
//     onClick={() => {
//       setShowProjectMembers(!showProjectMembers);
//     }}
//   >
//     <div className='w-full flex justify-between items-center'>
//       <div className='flex items-center gap-2 text-left'>
//         <div className='text-[1.2em] font-normal'><b>{projectMembersReceivingEmail.length}</b> Project Member{projectMembersReceivingEmail.length !== 1 ? 's' :'' }</div>
//       </div>
//       <div className='px-2 py-1'>
//         <div className={showProjectMembers ? 'scale-[-1]' : ''}>
//           <ExpandDownIcon />
//         </div>
//       </div>
//     </div>
//   </PushButton>
//   <CollapsibleContainer isOpen={showProjectMembers}>
//     <div className='flex flex-col gap-4 py-4 bg-[#444] rounded-b-lg overflow-y-auto mx-[1px]'>
//       {projectMembersReceivingEmail.map((user, index) => <div key={index} className='ml-4'><UserAvatar user={user} showOnlineStatus={false} /></div> )}
//     </div>
//   </CollapsibleContainer>
// </div>
// 
// {selectedProject.contactCount === 0 
//   ?
//     <div className={`flex flex-col justify-start items-start gap-2 bg-neutral-700 p-6 rounded-lg`}>
//       <div className='text-left'>Want others to stay in the loop?<br/>Add any email address and they’ll get a copy when you publish this schedule.</div>
//       <PushButton 
//         textColor='black'
//         bgColor='#f9a928'
//         hoverBgColor='#f79800'
//         onClick={() => {
//           setCurrentScreen(PAGE_ADD_EMAILS);
//         }}
//       >
//         <div className='flex items-center gap-1' ><AddIcon color='#000' /> Add Emails to Distribution List</div>
//       </PushButton>
//     </div>
//   :
//     <div className='flex flex-col gap-1'>
//       <div className='w-full flex flex-col'>
//         <PushButton 
//           textColor='white'
//           bgColor={emailContactsListOpen ? '#555' : '#444'}
//           hoverBgColor='#555'
//           dropdownButton={true}
//           dropdownOpen={emailContactsListOpen}
//           onClick={() => {
//             setEmailContactsListOpen(!emailContactsListOpen);
//           }}
//         >
//           <div className='w-full flex justify-between items-center'>
//             <div className='flex items-center gap-2 text-left'>
//               <div className='text-[1.2em] font-normal'><b>{selectedProject.contactCount}</b> Distribution List</div>
//             </div>
//             <div className='px-2 py-1'>
//               <div className={emailContactsListOpen ? 'scale-[-1]' : ''}>
//                 <ExpandDownIcon />
//               </div>
//             </div>
//           </div>
//         </PushButton>
//         <CollapsibleContainer isOpen={emailContactsListOpen}>
//           <div className='overflow-y-auto mx-[1px] bg-neutral-700 py-2 rounded-b-lg'>
//             <ProjectContacts projectId={selectedProject.id} subscriptionId={selectedProject.subscriptionId} scheduleId={selectedSchedule.id} />
//           </div>
//         </CollapsibleContainer>
//       </div>
//       <PushButton 
//         textColor='black'
//         bgColor='#f9a928'
//         hoverBgColor='#f79800'
//         onClick={() => {
//           setCurrentScreen(PAGE_ADD_EMAILS);
//         }}
//       >
//         <div className='flex items-center gap-1' ><AddIcon color='#000' /> Add Emails to Distribution List</div>
//       </PushButton>
//     </div>
// }
